import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Shield, AlertTriangle, FileText, Users, CreditCard, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

function TermsAndConditions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if there's a hash in the URL for anchor scrolling
    if (location.hash === '#refund-policy') {
      setTimeout(() => {
        document.getElementById('refund-policy')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      // Scroll to top on page load
      // scroll handled by <ScrollRestoration />
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-50">
        <div className="container mx-auto px-6 md:px-12 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-[#00F0FF] hover:text-[#00C2CC]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Marketplace
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-12 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-full bg-[#00F0FF]/10 flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-[#00F0FF]" />
            </div>
            <h1 className="text-4xl font-bold mb-4">Terms & Conditions</h1>
            <p className="text-neutral-400">Last updated: December 2025</p>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* Introduction */}
            <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <Shield className="w-6 h-6 text-[#00F0FF]" />
                1. Introduction
              </h2>
              <p className="text-neutral-300 leading-relaxed mb-4">
                Welcome to theailedger.ai ("Platform", "we", "us", or "our"). By accessing or using our marketplace, 
                you agree to be bound by these Terms and Conditions. Please read them carefully before using our services.
              </p>
              <p className="text-neutral-300 leading-relaxed">
                theailedger.ai is a digital marketplace connecting AI product creators ("Sellers") with buyers ("Buyers"). 
                We facilitate transactions but are not a party to the actual sale agreements between Sellers and Buyers.
              </p>
            </section>

            {/* User Accounts */}
            <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <Users className="w-6 h-6 text-white" />
                2. User Accounts
              </h2>
              <div className="space-y-4 text-neutral-300 leading-relaxed">
                <p><strong className="text-white">2.1 Account Creation:</strong> You must provide accurate, complete, and current information when creating an account. You are responsible for maintaining the confidentiality of your login credentials.</p>
                <p><strong className="text-white">2.2 Account Types:</strong> Users may register as Buyers or Sellers. Sellers must complete additional verification, including connecting a valid payment account.</p>
                <p><strong className="text-white">2.3 Age Requirement:</strong> You must be at least 18 years old to create an account and use our services.</p>
              </div>
            </section>

            {/* Seller Terms */}
            <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <CreditCard className="w-6 h-6 text-green-500" />
                3. Seller Terms
              </h2>
              <div className="space-y-4 text-neutral-300 leading-relaxed">
                <p><strong className="text-white">3.1 Product Listings:</strong> Sellers are solely responsible for the accuracy of their product descriptions, pricing, and any claims made about their products.</p>
                <p><strong className="text-white">3.2 Intellectual Property:</strong> Sellers warrant that they own or have proper licenses for all content they upload and sell on the Platform.</p>
                <p><strong className="text-white">3.3 Product Delivery:</strong> Sellers must deliver products to Buyers immediately upon successful payment. Digital products should be made available for instant download.</p>
                <p><strong className="text-white">3.4 Platform Fees:</strong> A 5% platform fee applies to all transactions. Affiliates may earn commissions as set by individual Sellers.</p>
                
                {/* Tax Information */}
                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="text-white font-bold text-lg mb-3">3.5 Tax Information & 1099 Reporting (US Sellers)</p>
                  <div className="bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-xl p-4 mb-4">
                    <p className="text-[#00F0FF] text-sm">
                      <strong>Important for US-based Sellers:</strong> Federal tax law requires us to report payments to the IRS.
                    </p>
                  </div>
                  <ul className="list-disc list-inside ml-4 space-y-2 text-sm">
                    <li><strong className="text-white">W-9 Requirement:</strong> US-based Sellers must provide valid W-9 information (legal name, address, and SSN or EIN) during the Stripe Connect onboarding process.</li>
                    <li><strong className="text-white">1099-NEC Issuance:</strong> If you earn <strong className="text-[#00F0FF]">$600 or more</strong> in a calendar year, you will receive a 1099-NEC form reporting your earnings to the IRS.</li>
                    <li><strong className="text-white">Delivery:</strong> 1099 forms are delivered electronically via email by January 31st of the following year. Paper copies are available upon request.</li>
                    <li><strong className="text-white">Accuracy:</strong> You are responsible for ensuring your tax information is accurate and up-to-date. Incorrect information may result in backup withholding or payment holds.</li>
                    <li><strong className="text-white">Tax Advice:</strong> theailedger.ai does not provide tax advice. Please consult a qualified tax professional regarding your tax obligations.</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Refund Policy - JVZOO Style */}
            <section id="refund-policy" className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-yellow-500" />
                4. Refund Policy
              </h2>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 mb-6">
                <p className="text-yellow-200 font-semibold mb-2">Important Notice</p>
                <p className="text-neutral-300">
                  Refund requests are handled directly between Buyers and Sellers. theailedger.ai acts as a facilitator 
                  and does not process refunds on behalf of Sellers.
                </p>
              </div>
              <div className="space-y-4 text-neutral-300 leading-relaxed">
                <p><strong className="text-white">4.1 Seller Responsibility:</strong> Each Seller is responsible for establishing and honoring their own refund policy. Buyers should review the Seller's refund policy before making a purchase.</p>
                <p><strong className="text-white">4.2 Dispute Resolution:</strong> In case of a dispute regarding refunds, Buyers should first contact the Seller directly. If resolution cannot be reached, Buyers may contact theailedger.ai support for assistance in mediation.</p>
                <p><strong className="text-white">4.3 Chargeback Policy:</strong> Initiating a chargeback with your payment provider without first attempting to resolve the issue with the Seller may result in account suspension or termination.</p>
                <p><strong className="text-white">4.4 Digital Products:</strong> Due to the nature of digital products, all sales may be considered final at the Seller's discretion. However, we encourage Sellers to offer reasonable refund windows for customer satisfaction.</p>
              </div>
            </section>

            {/* Account Termination */}
            <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <Ban className="w-6 h-6 text-red-500" />
                5. Account Termination
              </h2>
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 mb-6">
                <p className="text-red-300 font-semibold">theailedger.ai reserves the right to suspend or terminate accounts for violations of these terms.</p>
              </div>
              <div className="space-y-4 text-neutral-300 leading-relaxed">
                <p><strong className="text-white">5.1 Grounds for Termination:</strong> Accounts may be terminated for:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Selling counterfeit, fraudulent, or unauthorized products</li>
                  <li>Misrepresentation of products or services</li>
                  <li>Harassment or abuse of other users</li>
                  <li>Violation of intellectual property rights</li>
                  <li>Excessive chargebacks or fraudulent activity</li>
                  <li>Failure to deliver products to Buyers</li>
                  <li>Any violation of these Terms & Conditions</li>
                </ul>
                <p><strong className="text-white">5.2 Effect of Termination:</strong> Upon termination, Sellers will no longer receive payouts for pending transactions, and access to all Platform features will be revoked.</p>
                <p><strong className="text-white">5.3 Appeal Process:</strong> Users may appeal account termination by contacting support within 14 days of the termination notice.</p>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4">6. Limitation of Liability</h2>
              <div className="space-y-4 text-neutral-300 leading-relaxed">
                <p>theailedger.ai is provided "as is" without warranties of any kind. We are not liable for:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>The quality, safety, or legality of products listed</li>
                  <li>The truth or accuracy of product listings</li>
                  <li>The ability of Sellers to sell or Buyers to pay</li>
                  <li>Any indirect, incidental, or consequential damages</li>
                </ul>
              </div>
            </section>

            {/* Privacy */}
            <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4">7. Privacy</h2>
              <p className="text-neutral-300 leading-relaxed">
                Your use of theailedger.ai is subject to our Privacy Policy, which describes how we collect, 
                use, and share your information. By using the Platform, you consent to our data practices as described 
                in the Privacy Policy.
              </p>
            </section>

            {/* Changes to Terms */}
            <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4">8. Changes to Terms</h2>
              <p className="text-neutral-300 leading-relaxed">
                We may modify these Terms at any time. Changes will be effective upon posting to the Platform. 
                Your continued use of theailedger.ai after changes are posted constitutes acceptance of the modified Terms.
              </p>
            </section>

            {/* Affiliate Relationships & Disclosure */}
            <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8" id="affiliate-disclosure">
              <h2 className="text-2xl font-bold mb-4">9. Affiliate Relationships &amp; Featured Tools</h2>
              <div className="space-y-4 text-neutral-300 leading-relaxed">
                <p>
                  <strong className="text-white">9.1 Featured Partner listings:</strong> theailedger.ai curates a section
                  of "Featured AI Tools" on our <a href="/partners" className="text-[#00F0FF] underline">/partners</a> directory
                  that showcases third-party AI companies. Some of these listings are promoted through affiliate or
                  partner programs (for example, via Dub Partners). When you click a featured tool and sign up, we
                  may earn a commission from the third-party company — at no extra cost to you. Many partners also
                  offer theailedger.ai visitors a discount (e.g., "10% off for life").
                </p>
                <p>
                  <strong className="text-white">9.2 Our editorial standard:</strong> we only feature tools we would
                  recommend independently of any commercial relationship. Featured listings go through the same manual
                  vetting process as our paid Verified Enterprise Partners. A commission arrangement never changes
                  whether a product is listed, only whether we earn a referral fee.
                </p>
                <p>
                  <strong className="text-white">9.3 Disclosure placement:</strong> each affiliate-linked card on our
                  directory carries a "Featured partner" marker and a disclosure on hover, and our footer carries a
                  site-wide disclosure. This clause provides an additional written record of those relationships, as
                  required by the Federal Trade Commission's Endorsement Guides.
                </p>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4">10. Contact Us</h2>
              <p className="text-neutral-300 leading-relaxed mb-4">
                If you have questions about these Terms & Conditions, please contact us at:
              </p>
              <div className="bg-neutral-800/50 rounded-xl p-4">
                <p className="text-[#00F0FF]">support@theailedger.ai</p>
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="mt-12 text-center">
            <p className="text-neutral-500 text-sm">
              By using theailedger.ai, you acknowledge that you have read, understood, and agree to be bound by these Terms & Conditions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsAndConditions;
